import React, { useCallback, useContext, useEffect } from "react";
import EditorViews from "../components/editors/EditorViews.jsx";
import BryntumContext, {
  BryntumContextProvider,
  getSetting,
  userPreferences
} from "../context/bryntumContext.jsx";
import AdminMainRoutes from "../routes/AdminMainRoutes.jsx";
import AuthContext from "../context/authContext.jsx";
import AppHeader from "../components/appHeader/AppHeader.jsx";
import PreviewPage from "../pages/PreviewPage.jsx";
import { SWIVU_HOME_VIEW } from "../../other/constants/OtherKeys.jsx";
import useDisplayHomeModal from "../hooks/useDisplayHomeModal.jsx";
import { getSearchParam } from "../../other/utils/utils.jsx";
import { SwivuDevTools } from "../../../swivuDevTool.jsx";
import useCacheBuster from "../hooks/useCacheBuster.js";
import { Toaster } from "@/modern/components/ui/sonner";
import { EntityManagerView } from "../components/entityManager/EntityManagerView";

const intervalTimeInSec = 1800 * 1000;

let intervalId = null;
const { SWIVU_ENV } = import.meta.env;

const AdminLayout = () => {
  const user = useContext(AuthContext);
  const bryntumContext = useContext(BryntumContext);
  const authContext = useContext(AuthContext);
  const { isAppLoaded, entityManager } = bryntumContext;
  const { isOpen: isEntityManagerOpen } = entityManager;
  const { setDisplayAuthForm, displayAuthForm } = authContext;
  useDisplayHomeModal();
  useCacheBuster(isAppLoaded);
  const applyFontWeight = useCallback((isApply) => {
    if (isApply) document.body.classList.add("swivu-font-weight");
    else document.body.classList.remove("swivu-font-weight");
  }, []);
  useEffect(() => {
    if (isAppLoaded && userPreferences) {
      applyFontWeight(getSetting("fontType") === "Heavy");
      userPreferences.on("change", ({ record }) => {
        if (record?.key === "fontType") {
          applyFontWeight(record.value === "Heavy");
        }
      });
    }
  }, [applyFontWeight, isAppLoaded]);

  useEffect(() => {
    if (user?.isDemoUser && !displayAuthForm) {
      if (!intervalId) {
        intervalId = setInterval(() => {
          setDisplayAuthForm({ type: SWIVU_HOME_VIEW.auth });
        }, intervalTimeInSec);
      }
    }
    return () => {
      if (isAppLoaded && user?.isDemoUser) {
        clearInterval(intervalId);
      }
    };
  }, [displayAuthForm, user?.isDemoUser, setDisplayAuthForm, isAppLoaded]);

  // This is here because of when logged user add params manually in url
  // than on logout modal not showing because of params in url.
  useEffect(() => {
    if (
      isAppLoaded &&
      user.initialized &&
      user.user &&
      getSearchParam("guest-board")
    ) {
      const url = new URL(window.location.href);
      url.searchParams.delete("guest-board");
      window.history.replaceState(
        {},
        document.title,
        url.pathname + url.search
      );
    }
  }, [isAppLoaded, user.user, user.initialized]);

  return (
    <div className="AdminLayout flex flex-col h-screen overflow-hidden">
      {user.initialized && user.user && <AppHeader />}
      <div className="flex flex-1 min-h-0 relative">
        <div className="swivu-main-container min-h-0">
          <div className="AdminLayout-content flex-grow min-h-0">
            <div className="MainView h-full">
              <div className="flex flex-col flex-1 overflow-hidden flex-nowrap content-section routes-wrapper">
                {isAppLoaded && <>
                  <AdminMainRoutes />
                  {isEntityManagerOpen && <EntityManagerView />}
                </>}
                {user.initialized && !user.user && <PreviewPage />}
              </div>
            </div>
          </div>
        </div>
        {isAppLoaded && <EditorViews />}
        {/* This is a utility dev tool component and can be removed from here */}
        {SWIVU_ENV !== "production" && (
          <SwivuDevTools
            data={{
              bryntumContext,
              authContext
            }}
          />
        )}
      </div>
    </div>
  );
};

const AdminLayoutWrapper = () => (
  <BryntumContextProvider>
    <AdminLayout />
    <Toaster />
  </BryntumContextProvider>
);

export default AdminLayoutWrapper;
